import { useState, useEffect } from "react";
import { HowToPlay } from "../pages/HowToPlay";
import { useLocale } from "../lib/i18n";
import { LocalePicker } from "./LocalePicker";

interface Props {
  onComplete: () => void;
}

const ONBOARDING_STYLES = `
  @keyframes nig-fade-in {
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes nig-scale-in {
    from { transform: scale(0.9); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
  }
  @keyframes nig-progress {
    0% { width: 0%; }
    100% { width: 100%; }
  }
  .nig-fade-in { animation: nig-fade-in 0.6s ease-out both; }
  .nig-fade-in-d1 { animation: nig-fade-in 0.6s ease-out 0.2s both; }
  .nig-fade-in-d2 { animation: nig-fade-in 0.6s ease-out 0.4s both; }
  .nig-scale-in { animation: nig-scale-in 0.6s ease-out both; }
  .nig-progress { animation: nig-progress 2.5s linear forwards; }
`;

function SplashScreen({ onDone }: { onDone: () => void }) {
  const { t } = useLocale();

  useEffect(() => {
    const timer = setTimeout(onDone, 2500);
    return () => clearTimeout(timer);
  }, [onDone]);

  const GOLD = "#d4a24a";
  const GOLD_HI = "#f1d286";
  const GOLD_LO = "#8c6421";
  const NAVY = "#0b1f4a";

  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center overflow-hidden cursor-pointer"
      style={{
        background: `
          radial-gradient(ellipse at 50% 30%, var(--navy-700) 0%, var(--navy-900) 50%, var(--bg-deep) 100%)
        `,
      }}
      onClick={onDone}
    >
      {/* radial gold glow */}
      <div
        className="absolute pointer-events-none"
        style={{
          top: "25%",
          left: "50%",
          transform: "translateX(-50%)",
          width: 480,
          height: 480,
          borderRadius: "50%",
          background: `radial-gradient(circle, rgba(212,162,74,0.2) 0%, transparent 65%)`,
        }}
      />

      {/* top ornamental border */}
      <svg
        width="100%"
        height="32"
        viewBox="0 0 400 32"
        preserveAspectRatio="none"
        style={{ marginTop: 56, position: "relative", zIndex: 2 }}
        aria-hidden="true"
      >
        <path
          d="M 20 16 Q 100 4, 200 16 T 380 16"
          stroke={GOLD}
          strokeWidth="0.8"
          fill="none"
        />
        <circle cx="200" cy="16" r="3" fill={GOLD_HI} />
        <circle cx="40" cy="16" r="1.5" fill={GOLD} />
        <circle cx="360" cy="16" r="1.5" fill={GOLD} />
      </svg>

      {/* hero medallion with ensemble image */}
      <div
        className="nig-scale-in relative z-[3] mt-6"
        style={{ width: 260, height: 260 }}
      >
        {/* outer gold ring + vignette */}
        <div
          className="absolute inset-0 rounded-full"
          style={{
            border: `2px solid ${GOLD}`,
            boxShadow: `
              0 0 0 1px ${NAVY},
              0 0 0 5px rgba(140, 100, 33, 0.4),
              0 0 40px rgba(212, 162, 74, 0.35),
              inset 0 0 30px rgba(11, 31, 74, 0.67)
            `,
          }}
        />
        {/* ensemble image, circular crop */}
        <div
          className="absolute rounded-full"
          style={{
            inset: 6,
            overflow: "hidden",
            backgroundImage: `url("/reference-ensemble.jpg")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        {/* tiny gold center dot reminiscent of a record label */}
        <div
          className="absolute"
          style={{
            left: "50%",
            bottom: -10,
            transform: "translateX(-50%)",
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: GOLD_HI,
            boxShadow: `0 0 10px ${GOLD}`,
          }}
        />
      </div>

      {/* text block: tagline */}
      <div
        className="nig-fade-in-d1 mt-8 z-10 text-center"
        style={{
          fontFamily: "'Heebo', sans-serif",
          fontSize: 13,
          color: GOLD,
          letterSpacing: "0.3em",
          fontWeight: 500,
        }}
      >
        {t("onboarding.tagline")}
      </div>

      <div
        className="nig-fade-in-d2 mt-2 text-center z-10"
        style={{
          fontFamily: "'Heebo', sans-serif",
          fontSize: 11,
          color: "rgba(250, 245, 232, 0.5)",
          letterSpacing: "0.2em",
        }}
      >
        {t("common.loading")}…
      </div>

      {/* bottom ornamental border */}
      <svg
        width="100%"
        height="32"
        viewBox="0 0 400 32"
        preserveAspectRatio="none"
        style={{ position: "absolute", bottom: 40, zIndex: 2 }}
        aria-hidden="true"
      >
        <path
          d="M 20 16 Q 100 28, 200 16 T 380 16"
          stroke={GOLD}
          strokeWidth="0.8"
          fill="none"
        />
        <circle cx="200" cy="16" r="3" fill={GOLD_HI} />
      </svg>

      {/* progress bar */}
      <div
        className="nig-fade-in-d2 absolute bottom-16 z-10 w-full max-w-[220px] px-5"
      >
        <div
          className="w-full h-[2px] rounded-full overflow-hidden"
          style={{ background: "rgba(212,162,74,0.15)" }}
        >
          <div
            className="h-full nig-progress"
            style={{
              background: `linear-gradient(90deg, ${GOLD_LO}, ${GOLD_HI}, ${GOLD_LO})`,
              transformOrigin: "left",
            }}
          />
        </div>
      </div>

      <style>{ONBOARDING_STYLES}</style>
    </div>
  );
}

export function Onboarding({
  onComplete,
  skipSplash,
}: Props & { skipSplash?: boolean }) {
  const { t, locale, setLocale, isRTL } = useLocale();
  const [showSplash, setShowSplash] = useState(!skipSplash);

  if (showSplash) {
    return <SplashScreen onDone={() => setShowSplash(false)} />;
  }

  const handleComplete = () => {
    localStorage.setItem("nigunim:onboarded", "true");
    onComplete();
  };

  return (
    <>
      <style>{ONBOARDING_STYLES}</style>
      <div
        dir={isRTL ? "rtl" : "ltr"}
        className="fixed inset-0 z-[9999] flex flex-col overflow-y-auto overflow-x-hidden"
        style={{
          background: `
            radial-gradient(ellipse at 20% 0%, rgba(212, 162, 74, 0.12) 0%, transparent 55%),
            radial-gradient(ellipse at 80% 100%, rgba(16, 42, 102, 0.6) 0%, transparent 60%),
            var(--bg)
          `,
        }}
      >
        <div className="relative w-full max-w-[430px] mx-auto px-5 pt-6 pb-24 z-10">
          <div className="flex justify-end mb-4">
            <LocalePicker
              locale={locale}
              setLocale={setLocale}
              buttonClassName="w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold transition-colors"
            />
          </div>
          <HowToPlay />
          <div className="mt-6 flex flex-col items-center gap-3">
            <button
              onClick={handleComplete}
              className="w-full h-14 btn-primary rounded-2xl flex items-center justify-center active:scale-95 transition-transform"
            >
              <span
                className="font-serif text-lg"
                style={{ fontWeight: 700, letterSpacing: "0.08em" }}
              >
                {t("onboarding.letsPlay")}
              </span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export { SplashScreen, ONBOARDING_STYLES };

export function needsOnboarding(): boolean {
  return !localStorage.getItem("nigunim:onboarded");
}
